import { useState } from 'react'
import { Icon } from '@iconify/react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { Seo } from '@/seo/Seo'
import { useStorefrontContent } from '@/branding/BrandingProvider'

interface PatientFormData {
  firstName: string
  lastName: string
  dob: string
  gender: string
  measurementSystem: 'metric' | 'imperial'
  height: string
  weight: string
  phone: string
  email: string
  password: string
}

export function SignUpPatientPage() {
  const navigate = useNavigate()
  const { auth } = useStorefrontContent()
  const roleTitle = auth?.accountType?.roles?.patient?.title || 'Patient'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState<PatientFormData>({
    firstName: '',
    lastName: '',
    dob: '',
    gender: '',
    measurementSystem: 'metric',
    height: '',
    weight: '',
    phone: '',
    email: '',
    password: '',
  })

  const updateField = (field: keyof PatientFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      // Trim the email: keyboard autocomplete on mobile appends a space, which the
      // server rejects with "Email must be a valid email" — a 400 that used to
      // surface as an opaque "API Error 400: Bad Request". Password is deliberately
      // left out of this request: /registration ignores it, and it is sent for real
      // at /registration/password once the code is verified.
      const { email, password, ...profile } = form
      // See SignUpCaregiverPage: `emailSent: false` means the account is ready and only
      // delivery failed, so the verify screen offers Resend instead of a dead end.
      const signUp = await api.post<{ emailSent?: boolean }>('/registration', {
        ...profile,
        email: email.trim(),
        height: Number(form.height),
        weight: Number(form.weight),
        roleLabel: 'Patient',
      })
      navigate('/verify-otp', { state: { email: email.trim(), password, emailSent: signUp?.emailSent } })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6 py-12">
      <Seo
        title="Patient Sign Up — Zenzers Remote Monitoring & Alerts"
        description="Create a Zenzers patient account to monitor your health with continuous wearable telemetry, fall detection, and proactive alerts."
        path="/sign-up-patient"
      />
      <form onSubmit={handleSubmit} className="w-full max-w-lg bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
        <Link
          to="/account-type"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-navy mb-6 transition-colors"
        >
          <Icon icon="solar:arrow-left-linear" /> Back
        </Link>

        <h1 className="text-2xl font-medium tracking-tight text-navy mb-1">{roleTitle} Registration</h1>
        <p className="text-sm text-slate-500 font-light mb-6">Create your {roleTitle.toLowerCase()} account to get started.</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
            <input
              type="text"
              required
              value={form.firstName}
              onChange={(e) => updateField('firstName', e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
            <input
              type="text"
              required
              value={form.lastName}
              onChange={(e) => updateField('lastName', e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date of Birth</label>
            <input
              type="date"
              required
              value={form.dob}
              onChange={(e) => updateField('dob', e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Gender</label>
            <select
              required
              value={form.gender}
              onChange={(e) => updateField('gender', e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none bg-white"
            >
              <option value="">Select...</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer-not-to-say">Prefer not to say</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Measurement System</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="measurement"
                value="metric"
                checked={form.measurementSystem === 'metric'}
                onChange={() => updateField('measurementSystem', 'metric')}
                className="accent-navy"
              />
              Metric (cm, kg)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="measurement"
                value="imperial"
                checked={form.measurementSystem === 'imperial'}
                onChange={() => updateField('measurementSystem', 'imperial')}
                className="accent-navy"
              />
              Imperial (in, lbs)
            </label>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Height ({form.measurementSystem === 'metric' ? 'cm' : 'in'})
            </label>
            <input
              type="number"
              required
              value={form.height}
              onChange={(e) => updateField('height', e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Weight ({form.measurementSystem === 'metric' ? 'kg' : 'lbs'})
            </label>
            <input
              type="number"
              required
              value={form.weight}
              onChange={(e) => updateField('weight', e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
          <input
            type="tel"
            required
            value={form.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => updateField('email', e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
          <input
            type="password"
            required
            minLength={8}
            value={form.password}
            onChange={(e) => updateField('password', e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-navy/20 focus:border-navy outline-none"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-navy text-white py-3 rounded-lg font-medium text-sm hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>
    </div>
  )
}
